import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/Orders_UpdateItemDeliveryDateInput';
import '../contracts/Orders_UpdateItemDeliveryDateResponse';
import '../contracts/Orders_ItemDeliveryDateHistoryResponse';

/**
 * Composable for managing SO order item delivery-date operations.
 *
 * Handles fetching delivery-date history and updating the expected
 * delivery date for a service parent item.
 */
export function useSODeliveryDate() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/orders';

  /**
   * Fetch delivery-date change history for an order item, newest first.
   */
  const fetchHistory = async (itemId: number) => {
    log('useSODeliveryDate', 'fetchHistory', 'Fetching delivery date history', { itemId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/item_delivery_date_history/${itemId}`, { responseContract: 'Orders_ItemDeliveryDateHistoryResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao buscar historico de data de entrega');
    } catch (err) {
      logError('useSODeliveryDate', 'fetchHistory', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update the expected delivery date for a service parent item.
   *
   * @param itemId The order item ID
   * @param data Object with expectedDeliveryAt (ISO 8601 UTC) and reason (non-blank)
   */
  const updateDeliveryDate = async (itemId: number, data: { expectedDeliveryAt: string; reason: string }) => {
    log('useSODeliveryDate', 'updateDeliveryDate', 'Updating delivery date', { itemId, data });
    isLoading.value = true;
    error.value = null;
    try {
      const payload = {
        expected_delivery_at: data.expectedDeliveryAt,
        reason: data.reason,
      };
      const response = await httpService.post(`${basePath}/update_item_delivery_date/${itemId}`, payload, {
        inputContract: 'Orders_UpdateItemDeliveryDateInput',
        responseContract: 'Orders_UpdateItemDeliveryDateResponse',
      });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao atualizar data de entrega');
    } catch (err) {
      logError('useSODeliveryDate', 'updateDeliveryDate', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchHistory,
    updateDeliveryDate,
  };
}
